import React from "react";
import {Box, Typography} from "@mui/material";
import { styled } from "@mui/material/styles";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Color variant for the TipCard. Each variant applies a themed background, border, and icon container styling.
 *
 * Card background (`CARD_STYLES`) and icon container (`ICON_STYLES`) are defined separately —
 * `info` shares the same indigo card background as `default` but uses a distinct blue icon:
 *
 * | Variant   | Card bg   | Card border | Icon bg   | Icon color |
 * |-----------|-----------|-------------|-----------|------------|
 * | `default` | `#f2f4ff` | `#c8d0f8`   | `#e8eeff` | `#3f4fc1`  |
 * | `info`    | `#f2f4ff` | `#c8d0f8`   | `#c8e8f8` | `#1088b8`  |
 * | `warn`    | `#fffcf0` | `#f0d87a`   | `#fde8a0` | `#b07d00`  |
 * | `success` | `#e8faf0` | `#a8dfbe`   | `#c4f0d4` | `#1a7040`  |
 * | `error`   | `#fdf2f2` | `#f5b8b8`   | `#fbbaba` | `#8a1010`  |
 */
export type TipCardVariant = "default" | "info" | "warn" | "success" | "error";

/**
 * Props for the {@link TipCard} component.
 */
export interface TipCardProps {
  /**
   * Color scheme variant. Defaults to `"default"`.
   */
  variant?: TipCardVariant;

  /**
   * Icon to display in the colored icon container (left side).
   * Typically, an MUI icon component (e.g., `<InfoIcon fontSize="small" />`).
   */
  icon: React.ReactNode;

  /**
   * Bold heading text displayed above the body.
   */
  title: string;

  /**
   * Body content rendered below the title. Callers are responsible for all
   * text styling (font size, color, line-height). Typically wrapped in a
   * `Typography` component. Supports any JSX including `<strong>` and `<a>` tags.
   */
  children: React.ReactNode;
}

// ─── Variant token maps ───────────────────────────────────────────────────────

/**
 * Background and border color tokens for each tip card variant.
 * Maps variant name to card-level styling.
 *
 * All borders are 1px solid. Card padding is always 14px × 16px.
 */
const CARD_STYLES: Record<TipCardVariant, { bg: string; border: string }> = {
  default: { bg: "#f2f4ff", border: "#c8d0f8" },
  info:    { bg: "#f2f4ff", border: "#c8d0f8" },
  warn:    { bg: "#fffcf0", border: "#f0d87a" },
  success: { bg: "#e8faf0", border: "#a8dfbe" },
  error:   { bg: "#fdf2f2", border: "#f5b8b8" },
};

/**
 * Icon container background and text color tokens for each variant.
 * The icon box is always 30×30px with 7px border-radius.
 *
 * Icons are rendered at font-size 13px (0.9286rem).
 */
const ICON_STYLES: Record<TipCardVariant, { bg: string; color: string }> = {
  default: { bg: "#e8eeff", color: "#3f4fc1" },
  info:    { bg: "#c8e8f8", color: "#1088b8" },
  warn:    { bg: "#fde8a0", color: "#b07d00" },
  success: { bg: "#c4f0d4", color: "#1a7040" },
  error:   { bg: "#fbbaba", color: "#8a1010" },
};

// ─── Styled primitives ────────────────────────────────────────────────────────

/**
 * Root container for the tip card. Applies variant-specific background and border,
 * and establishes the flex layout (icon + text content side-by-side).
 *
 * `height: 100%` ensures cards expand to fill their grid cell, enabling vertical alignment
 * across the 2-column responsive grid.
 */
const TipCardRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant",
})<{ ownerVariant: TipCardVariant }>(({ ownerVariant }) => ({
  backgroundColor: CARD_STYLES[ownerVariant].bg,
  border: `1px solid ${CARD_STYLES[ownerVariant].border}`,
  borderRadius: "6px",
  padding: "14px 16px",
  display: "flex",
  gap: "12px",
  alignItems: "flex-start",
  height: "100%",
}));

/**
 * Icon container box. Fixed 30×30px with variant-specific background and text color.
 * Renders icons at 13px font size (0.8125rem).
 *
 * `aria-hidden="true"` is applied because the icon is decorative; the semantic meaning
 * is conveyed by the title and body text.
 */
const TipIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerVariant",
})<{ ownerVariant: TipCardVariant }>(({ ownerVariant }) => ({
  width: 30,
  height: 30,
  borderRadius: "7px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  marginTop: "1px",
  backgroundColor: ICON_STYLES[ownerVariant].bg,
  color: ICON_STYLES[ownerVariant].color,
  fontSize: "0.9286rem",   // 13px
}));

/**
 * Card title text. Bold, 12.5px (0.8929rem), dark blue-grey (#1a2636).
 * `marginBottom: 3px` provides tight spacing above the body text.
 */
const TipTitle = styled(Typography)({
  fontSize: "0.8929rem",  // 12.5 px
  fontWeight: 700,
  color: "#1a2636",
  marginBottom: "3px",
});



// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared body text style for use inside {@link TipCard}.
 * Export this alongside `TipCard` so all consumers use a consistent
 * font size, color, line-height, and inline element treatment.
 *
 * @example
 * <TipCard variant="info" icon={<InfoIcon />} title="Heads up">
 *   <TipCardBody component="div">
 *     Must <strong>verify email</strong> first.
 *   </TipCardBody>
 * </TipCard>
 */
export const TipCardBody = styled(Box)(({ theme }) => ({
  fontSize: "0.8571rem",   // 12px
  color: "#5a7080",
  lineHeight: 1.45,
  "& *": { fontSize: "0.8571rem" },
  "& strong": { color: "#324559" },
  "& a": { color: theme.palette.primary.main, textDecoration: "underline" },
}));

/**
 * Informational tip card component that displays guidance, warnings, or success tips
 * in the InfoBanner collapsible section.
 *
 * The card consists of:
 * - A fixed-size icon container (30×30px) with variant-specific coloring
 * - A title (bold, 12.5px)
 * - Rich-text body content (12px, supports `<strong>` and links)
 *
 * All cards expand to fill
 * their grid cell, making the layout responsive and aligned.
 *
 * @param {TipCardProps} props - The component props.
 * @param {TipCardVariant} [props.variant="default"] - Color scheme. One of:
 *   - `"default"` - indigo/blue theme
 *   - `"info"` - bright blue theme
 *   - `"warn"` - amber/yellow theme
 *   - `"success"` - green theme
 * @param {React.ReactNode} props.icon - Icon element (typically MUI icon, e.g., `<InfoIcon fontSize="small" />`).
 *   Will be rendered at 13px font size and colored per variant.
 * @param {string} props.title - Bold heading text (max 1 line recommended).
 * @param {React.ReactNode} props.children - Body text. Supports JSX, strings, `<strong>`, and `<a>` tags.
 *   Text color is #5a7080 with #324559 for bold text.
 *
 * @returns {React.ReactElement} The rendered tip card.
 *
 * @example
 * // Info variant with an icon and rich text
 * <TipCard
 *   variant="info"
 *   icon={<InfoIcon fontSize="small" />}
 *   title="Email verification required"
 * >
 *   You must <strong>confirm your email address</strong> before you can create locations.
 *   Check your inbox for a link or{" "}
 *   <a href="/account/settings">resend it from Account Settings</a>.
 * </TipCard>
 */
const TipCard = ({ variant = "default", icon, title, children }: TipCardProps): React.ReactElement<any> => {
  return (
    <TipCardRoot ownerVariant={variant}>
      <TipIconBox ownerVariant={variant} aria-hidden="true">
        {icon}
      </TipIconBox>
      <Box>
        <TipTitle>{title}</TipTitle>
        {children}
      </Box>
    </TipCardRoot>
  );
};

export default TipCard;
